import { useMemo } from 'react';

import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useNextScheduledLesson from '@/data/hook/student-dashboard/useNextScheduledLesson';
import useListService from '@/data/hook/useListService';
import { studentDashboardQueries } from '@/data/query-keys/studentDashboardQueryKey';
import { courseLessonProgressFilters } from '@/utils/lesson-progress/courseLessonProgressFilters';
import { ROUTES } from '@/utils/routes';
import { toContinuityLesson } from '@/utils/student-dashboard/continuityLesson';
import { findNextLessonToDo } from '@/utils/student-dashboard/findNextLessonToDo';
import { toUnitProgress } from '@/utils/student-dashboard/unitProgress';

type UseStudentContinuityParams = {
  courseProgressId?: number;
  klassId?: number;
  slug?: string;
};

export default function useStudentContinuity({
  courseProgressId,
  klassId,
  slug,
}: UseStudentContinuityParams) {
  const {
    nextLesson,
    isLoading: isLoadingNextLesson,
    error: nextLessonError,
  } = useNextScheduledLesson({ klassId });

  const nextLessonBlockId = nextLesson?.lesson?.lessonBlock?.id;
  const shouldListCourseLessons = !isLoadingNextLesson && !nextLesson?.lesson;

  const {
    results: lessonProgresses,
    isInitialLoading: isLoadingLessonProgresses,
    error: lessonProgressesError,
  } = useListService({
    ...studentDashboardQueries.lessonProgresses({
      ...courseLessonProgressFilters(courseProgressId),
      lessonBlockId: nextLessonBlockId ? [nextLessonBlockId] : undefined,
    }),
    enabled:
      !!courseProgressId && (!!nextLessonBlockId || shouldListCourseLessons),
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const lesson = useMemo(() => {
    if (nextLesson?.lesson)
      return toContinuityLesson(nextLesson.lesson, nextLesson.order);

    const nextLessonToDo = findNextLessonToDo(lessonProgresses);

    return nextLessonToDo
      ? toContinuityLesson(
          nextLessonToDo.lesson,
          nextLessonToDo.scheduledLessonOrder ?? undefined,
        )
      : undefined;
  }, [nextLesson, lessonProgresses]);

  const unitProgress = useMemo(
    () => toUnitProgress(lessonProgresses, lesson?.unitId),
    [lesson, lessonProgresses],
  );

  const href =
    slug && lesson
      ? ROUTES.COURSES.LESSON.INFO(slug, lesson.lessonId, klassId)
      : undefined;

  return {
    lesson,
    unitProgress,
    href,
    isLoading: isLoadingNextLesson || isLoadingLessonProgresses,
    error: nextLessonError ?? lessonProgressesError,
  };
}
